import { useState, type FormEvent } from 'react'
import { useCreateComponent, useUpdateComponent } from '../hooks/useComponents'
import { useMe } from '../hooks/useMe'
import {
  ModalShell, Field, inputClass, ServerError,
  CancelButton, PrimaryButton, DangerButton
} from './modal-form'
import type {
  Component, ComponentCreate, Attachment,
  ComponentCategory, ComponentStatus
} from '../types'
import { COMPONENT_CATEGORIES } from '../types'

// ComponentEditor - one modal for both create and edit (`component` null =
// new). Mirrors ActivityEditor exactly in shape; the category-on-create case
// is the only meaningful difference, and that just plumbs through a prop so
// "Add to Locomotive" jumps straight to the right starting category.
//
// Financial gating: cost-estimate input renders for users with
// me.hasFinancial only. Non-financial users see a read-only "—" row instead.
// The server is the actual gate (handlers/components.ts drops costEstimate
// from the body for non-financial users); this is a UX improvement on top.

const STATUSES: ReadonlyArray<ComponentStatus> = ['Proposed', 'Confirmed', 'Cancelled']

interface Props {
  eventId: number
  component: Component | null   // null = creating new
  defaultCategory?: ComponentCategory   // category-scoped Add buttons can pre-select
  onClose: () => void
  onDelete?: () => void
  deleting?: boolean
}

const ATTACHMENT_URL_RE = /^(https?:\/\/|mailto:)/i

export function ComponentEditor({
  eventId, component, defaultCategory, onClose, onDelete, deleting
}: Props) {
  const isEditing = component !== null
  const { data: me } = useMe()
  const hasFinancial = me?.hasFinancial ?? false

  const createMut = useCreateComponent(eventId)
  const updateMut = useUpdateComponent(eventId)
  const mut = isEditing ? updateMut : createMut

  const [category, setCategory] = useState<ComponentCategory>(
    component?.category ?? defaultCategory ?? 'Other'
  )
  const [status, setStatus] = useState<ComponentStatus>(component?.status ?? 'Proposed')
  const [name, setName] = useState(component?.name ?? '')
  const [description, setDescription] = useState(component?.description ?? '')
  const [quantity, setQuantity] = useState(
    component?.quantity != null ? String(component.quantity) : ''
  )
  // Cost is rendered as a string in the input for sensible empty-state
  // handling. Empty string -> null on submit.
  const [costEstimate, setCostEstimate] = useState(
    component?.costEstimate != null ? component.costEstimate.toFixed(2) : ''
  )
  const [notes, setNotes] = useState(component?.notes ?? '')
  const [attachments, setAttachments] = useState<Attachment[]>(component?.attachments ?? [])

  const trimmedName = name.trim()
  const quantityNum = quantity === '' ? null : Number(quantity)
  const costNum = costEstimate === '' ? null : Number(costEstimate)

  const fieldErrors: { name?: string; quantity?: string; costEstimate?: string } = {}
  if (trimmedName.length === 0) fieldErrors.name = 'Name is required'
  else if (trimmedName.length > 255) fieldErrors.name = 'Name must be 255 characters or fewer'

  if (
    quantity !== '' &&
    (quantityNum === null || !Number.isInteger(quantityNum) || quantityNum < 0)
  ) {
    fieldErrors.quantity = 'Quantity must be a whole number (or leave blank)'
  }

  // Only validate cost if the user can actually edit it. For non-financial
  // users the input doesn't render and the value stays whatever it was.
  if (hasFinancial && costEstimate !== '') {
    if (costNum === null || !Number.isFinite(costNum) || costNum < 0) {
      fieldErrors.costEstimate = 'Cost must be a non-negative number (or leave blank)'
    } else if (costNum > 9_999_999_999.99) {
      fieldErrors.costEstimate = 'Cost is too large'
    }
  }

  const attachmentErrors = attachments.map((a) => {
    if (a.label.trim().length === 0) return 'Label required'
    if (a.url.trim().length === 0) return 'URL required'
    if (!ATTACHMENT_URL_RE.test(a.url.trim())) return 'URL must start with http://, https://, or mailto:'
    if (a.label.length > 255) return 'Label too long'
    if (a.url.length > 2000) return 'URL too long'
    return null
  })
  const hasAttachmentErrors = attachmentErrors.some((e) => e !== null)

  const isValid = Object.keys(fieldErrors).length === 0 && !hasAttachmentErrors
  const saving = mut.isPending

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!isValid || saving) return
    const body: ComponentCreate = {
      category,
      name: trimmedName,
      description: description.trim() === '' ? null : description,
      quantity: quantityNum,
      // For non-financial users we send null - the server preserves the
      // existing value on UPDATE via its CASE expression, and stores NULL
      // on INSERT (a non-financial user creating a new component just
      // won't have set a cost, which is the right default).
      costEstimate: hasFinancial ? costNum : null,
      notes: notes.trim() === '' ? null : notes,
      status,
      attachments: attachments.map((a) => ({
        label: a.label.trim(),
        url: a.url.trim()
      })),
      sortOrder: component?.sortOrder ?? 0
    }
    if (isEditing && component) {
      updateMut.mutate({ id: component.id, body }, { onSuccess: () => onClose() })
    } else {
      createMut.mutate(body, { onSuccess: () => onClose() })
    }
  }

  function addAttachment() {
    setAttachments((cur) => [...cur, { label: '', url: '' }])
  }
  function removeAttachment(idx: number) {
    setAttachments((cur) => cur.filter((_, i) => i !== idx))
  }
  function updateAttachment(idx: number, field: 'label' | 'url', value: string) {
    setAttachments((cur) => cur.map((a, i) => (i === idx ? { ...a, [field]: value } : a)))
  }

  return (
    <ModalShell
      title={isEditing ? 'Edit component' : 'Add component'}
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <>
          {onDelete && (
            <DangerButton onClick={onDelete} disabled={saving || deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </DangerButton>
          )}
          <CancelButton onClick={onClose} disabled={saving || deleting} />
          <PrimaryButton disabled={!isValid || saving || deleting}>
            {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Add component'}
          </PrimaryButton>
        </>
      }
      body={
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as ComponentCategory)}
                className={inputClass(false)}
              >
                {COMPONENT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
            <Field label="Status">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as ComponentStatus)}
                className={inputClass(false)}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Name" error={fieldErrors.name}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={255}
              autoFocus
              placeholder="e.g. Repton 926, Mark 1 set, Acoustic Roots Trio"
              className={inputClass(!!fieldErrors.name)}
            />
          </Field>

          <Field label="Description" hint="What is it / what's it for?">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className={`${inputClass(false)} resize-y font-body`}
              placeholder="Optional - any detail relevant to building or running this event."
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Quantity" error={fieldErrors.quantity}>
              <input
                type="number"
                min="0"
                step="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="—"
                className={inputClass(!!fieldErrors.quantity)}
              />
            </Field>
            {hasFinancial ? (
              <Field
                label="Cost estimate (£)"
                hint="Excl. VAT"
                error={fieldErrors.costEstimate}
              >
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={costEstimate}
                  onChange={(e) => setCostEstimate(e.target.value)}
                  placeholder="—"
                  className={inputClass(!!fieldErrors.costEstimate)}
                />
              </Field>
            ) : (
              <Field label="Cost estimate (£)" hint="Restricted">
                <div
                  className={`${inputClass(false)} bg-parchment text-ink-soft italic select-none`}
                  aria-readonly
                >
                  —
                </div>
              </Field>
            )}
          </div>

          <Field label="Notes" hint="Internal">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className={`${inputClass(false)} resize-y font-body`}
              placeholder="Optional - PO numbers, supplier contacts, anything not visible to volunteers."
            />
          </Field>

          {/* Attachments section - same shape as ActivityEditor */}
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft">
                Attachments
              </span>
              <button
                type="button"
                onClick={addAttachment}
                className="text-xs text-green font-semibold hover:underline"
              >
                + Add link
              </button>
            </div>
            {attachments.length === 0 ? (
              <div className="text-xs italic text-ink-soft bg-parchment border border-line rounded px-3 py-3">
                No attachments. Add a link to a contract, supplier brief, image, or anything else.
              </div>
            ) : (
              <div className="space-y-2">
                {attachments.map((a, idx) => {
                  const rowError = attachmentErrors[idx]
                  return (
                    <div key={idx}>
                      <div className="flex gap-2 items-start">
                        <input
                          type="text"
                          value={a.label}
                          onChange={(e) => updateAttachment(idx, 'label', e.target.value)}
                          placeholder="Label"
                          maxLength={255}
                          className={`${inputClass(rowError !== null)} flex-1`}
                        />
                        <input
                          type="url"
                          value={a.url}
                          onChange={(e) => updateAttachment(idx, 'url', e.target.value)}
                          placeholder="https://…"
                          maxLength={2000}
                          className={`${inputClass(rowError !== null)} flex-[2]`}
                        />
                        <button
                          type="button"
                          onClick={() => removeAttachment(idx)}
                          className="text-ink-soft hover:text-oxblood text-xl leading-none w-9 h-9 flex items-center justify-center rounded hover:bg-parchment flex-shrink-0"
                          aria-label="Remove attachment"
                        >
                          ×
                        </button>
                      </div>
                      {rowError && (
                        <div className="text-xs text-oxblood mt-1 ml-1">{rowError}</div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {mut.isError && (
            <ServerError
              title={`Could not ${isEditing ? 'save' : 'add'} component`}
              error={mut.error as Error}
            />
          )}
        </div>
      }
    />
  )
}
